import { Button } from "@a2z/ui";

import { endpoints } from "@/lib/api/endpoints";
import type { RestaurantSettings } from "@/lib/api/types";
import { useI18n, useTranslate } from "@/lib/i18n";
import { formatSectionStamp } from "@/modules/pos/lib/sectionTime";
import { queryKeys, useApiQuery } from "@/shared/queries";

import { RequestCard, RequestCardRow } from "@/modules/pos/components/RequestCard";

import { SERVICE_REQUEST_BADGES, SERVICE_REQUEST_ICONS, SERVICE_REQUEST_LABELS } from "./serviceRequests";
import { useMarkServiceRequestDone, useServiceRequests } from "./useServiceRequests";

/**
 * A bill's table asks (D132), above its lines: each open ask on the bill's
 * table(s), when it was made, and Done. Renders nothing when none is open, and
 * nothing for a bill at no table — an ask belongs to a table.
 */
export function TableServiceRequests({ tableIds }: { tableIds: readonly string[] }) {
  const t = useTranslate();
  const { locale } = useI18n();
  const asks = useServiceRequests();
  const { markDone, busyId } = useMarkServiceRequestDone();
  const settings = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    { retry: false },
  );

  const mine = (asks.data ?? []).filter((ask) => tableIds.includes(ask.tableId));
  if (mine.length === 0) return null;

  /* The same request card as a diner's QR order (Trioty canvas, "Floor
     alerts"): a non-food ask is still the table waiting on staff. One answer
     per ask — Done — and nothing to put off: an ask stays until somebody
     handles it. */
  return (
    <RequestCard
      tone="guest"
      badge={mine.length === 1 ? SERVICE_REQUEST_BADGES[mine[0].kind] : "servicebell"}
      testId="table-asks"
      title={t((m) => m.serviceRequests.billHeading)}
    >
      {mine.map((ask) => {
        const Icon = SERVICE_REQUEST_ICONS[ask.kind];
        return (
          <RequestCardRow
            key={ask.id}
            actions={
              <Button
                size="sm"
                variant="primary"
                data-testid={`table-ask-done-${ask.kind}`}
                disabled={busyId === ask.id}
                onClick={() => void markDone(ask.id)}
              >
                {t((m) => m.serviceRequests.done)}
              </Button>
            }
          >
            <span
              data-ui="table-ask"
              data-kind={ask.kind}
              data-testid={`table-ask-${ask.kind}`}
              className="flex items-center gap-2"
            >
              <Icon aria-hidden="true" size={16} strokeWidth={1.75} className="shrink-0" />
              <span className="flex min-w-0 flex-col">
                <span data-ui="table-ask-label" className="text-sm font-semibold">
                  {t(SERVICE_REQUEST_LABELS[ask.kind])}
                </span>
                <span data-ui="table-ask-time" className="text-xs tabular-nums text-[color:var(--text-muted)]">
                  {t((m) => m.serviceRequests.since, {
                    time: formatSectionStamp(
                      ask.requestedAt,
                      settings.data?.timezone,
                      locale === "en" ? "en" : "ar",
                    ),
                  })}
                </span>
              </span>
            </span>
          </RequestCardRow>
        );
      })}
    </RequestCard>
  );
}
